import { watch } from 'vue'

// ==========================================================================
// The words the service worker needs to show a push in your language.
//
// A push is shown by the service worker, with the app closed: no Vue, no
// vue-i18n, no catalogue. Until now it showed the English `title`/`message`
// the server built, so a Catalan phone vibrated in English. The bell does
// not have that problem because it translates `titleKey`/`messageKey` when it
// opens — the push carries the same keys (notifications/PushService.js).
//
// Translating on the server would need the catalogues there, and they are the
// frontend's (§4.1: the two halves never import each other; §16: the backend
// carries no customer language). So the catalogue travels the other way: this
// page, which has it merged and in the person's language, hands it to the
// service worker, which keeps it in Cache Storage and renders each push with
// it (public/pushText.js). It is the same catalogue the screens use, not a
// copy kept somewhere else.
//
// Once per build and language: the fingerprint is the build id plus the
// locale, and an unchanged one is not posted again. The language is the one
// the person chose (account preference, synced on sign-in), so a phone that
// has not opened the app since its owner switched language keeps the previous
// one until it does — the English fallback is never worse than before.
// ==========================================================================

const SENT_KEY = 'general-erp:push-catalogue'

/** Nested messages → `{ 'a.b.c': 'text' }`, strings only. */
export function flattenMessages(object, prefix = '', out = {}) {
  for (const [key, value] of Object.entries(object ?? {})) {
    const full = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'string') out[full] = value
    else if (value && typeof value === 'object' && !Array.isArray(value)) flattenMessages(value, full, out)
  }
  return out
}

function remembered() {
  try { return localStorage.getItem(SENT_KEY) } catch { return null }
}

function remember(fingerprint) {
  try { localStorage.setItem(SENT_KEY, fingerprint) } catch { /* private mode: it is sent again next time */ }
}

/**
 * @param {object} i18n   the app's vue-i18n instance (composition mode)
 * @param {object} [options]
 * @param {string} [options.build]  this build's id: a new build may have new words
 * @param {object} [options.worker] navigator.serviceWorker, for the tests
 */
export function shareCatalogueWithWorker(i18n, { build = 'dev', worker = globalThis.navigator?.serviceWorker } = {}) {
  if (!worker?.ready) return () => {}

  const send = async (locale) => {
    const fingerprint = `${build}:${locale}`
    if (remembered() === fingerprint) return
    const registration = await worker.ready
    if (!registration?.active) return
    registration.active.postMessage({
      type: 'PUSH_CATALOGUE',
      locale,
      fingerprint,
      messages: flattenMessages(i18n.global.getLocaleMessage(locale)),
    })
    remember(fingerprint)
  }

  return watch(() => i18n.global.locale.value, (locale) => {
    send(locale).catch(() => { /* no worker yet: the next start sends it */ })
  }, { immediate: true })
}
